/**
 * @typedef {Object} BlockSectionType
 * @property {string} title - The title of the block section
 * @property {string} id - The unique identifier for the section
 * @property {string} colorScheme - The color scheme associated with the section
 * @property {string[]} [blocks] - Optional array of block identifiers belonging to this section
 */

/** @type {BlockSectionType[]} */
const BLOCK_SECTIONS = [
    {
        title: "Model",
        id: "model",
        colorScheme: "purboflow",
        icon: "far fa-chart-network",
        description: "Run a fine-tuned or foundational vision model."
    },
    {
        title: "Visualization",
        id: "visualization",
        colorScheme: "blue",
        icon: "far fa-object-group",
        description: "Visualize the output of a model."
    },
    {
        title: "Logic and Branching",
        id: "flow_control",
        colorScheme: "yellow",
        icon: "far fa-code-branch",
        description: "Control the flow of your workflow."
    },
    {
        title: "Data Storage",
        id: "data_storage",
        colorScheme: "pink",
        icon: "far fa-database",
        description: "Save data in a Roboflow dataset or an external database."
    },
    {
        title: "Notification",
        id: "notifications",
        colorScheme: "salmon",
        icon: "far fa-bell",
        description: "Send an alert, such as an SMS message or email."
    },
    {
        title: "Video",
        id: "video",
        colorScheme: "indigo",
        icon: "far fa-video",
        description:
            "Perform stateful video analysis, such as tracking unique objects, time in zone, and line crossing."
    },
    {
        title: "Transformation",
        id: "transformation",
        colorScheme: "green",
        icon: "far fa-crop-alt",
        description:
            "Manipulate image and prediction data, such as cropping, stitching images, and offsetting predictions."
    },
    {
        title: "Classical Computer Vision",
        id: "classical_cv",
        colorScheme: "cyan",
        icon: "far fa-aperture",
        description:
            "Perform classical computer vision tasks, such as edge detection, template matching, and size measurement."
    },
    {
        title: "Advanced",
        id: "advanced",
        colorScheme: "orange",
        icon: "far fa-network-wired",
        description:
            "Leverage advanced features, such as pass/fail logic, data caching, and embedding similarity."
    },
    {
        title: "Industrial Integration",
        id: "industrial",
        colorScheme: "purple",
        icon: "far fa-industry",
        description:
            "Connect to industrial systems, PLCs, and IoT protocols for factory automation."
    },
    {
        title: "Custom",
        id: "custom",
        colorScheme: "brown",
        icon: "far fa-code",
        description:
            "Create custom blocks to perform any task that our default blocks don't support."
    }
];

const detection_types = [
    "Batch[object_detection_prediction]",
    "Batch[instance_segmentation_prediction]",
    "Batch[keypoint_detection_prediction]",
    "object_detection_prediction",
    "instance_segmentation_prediction",
    "keypoint_detection_prediction"
];

const classification_types = ["Batch[classification_prediction]", "classification_prediction"];

const detection_types_array = detection_types.map((type) => `${type}[]`);

const copyImageWarning = {
    property: "copy_image",
    value: false,
    message: [
        "This setting will mutate its input image.",
        "If the input is used by other blocks,",
        "it may cause unexpected behavior."
    ].join(" ")
};

/** @type {Object.<string, Object>} */
const BLOCK_UI_SPECIFICATION = {
    // ENTERPRISE BLOCKS - UI only definitions for visibility
    // These blocks will get their full schema from backend when LOAD_ENTERPRISE_BLOCKS=True
    // IMPORTANT: Keys MUST match backend block identifiers (without @v1) to prevent duplicates!
    "mqtt_writer_sink": {
        name: "MQTT Writer",
        blockPriority: 10,
        icon: "fal fa-network-wired",
        section: "industrial",
        short_description: "Publish data to MQTT broker",
        long_description:
            "Send workflow results to an MQTT broker for real-time data streaming and IoT integrations.",
        enterprise_only: true,
        local_only: true,
        ui_only: true // Flag to indicate this is UI-only until backend loads it
    },
    "roboflow_enterprise/opc_writer_sink": {
        name: "OPC UA Writer",
        blockPriority: 11,
        icon: "fal fa-industry",
        section: "industrial",
        short_description: "Write data to OPC UA servers",
        long_description:
            "Connect to industrial OPC UA servers for automation and control systems integration.",
        enterprise_only: true,
        local_only: true,
        ui_only: true
    },
    "roboflow_core/microsoft_sql_server_sink": {
        name: "Microsoft SQL Server",
        blockPriority: 3,
        icon: "fal fa-database",
        section: "data_storage",
        short_description: "Store results in SQL Server",
        long_description: "Save workflow outputs directly to Microsoft SQL Server database tables.",
        enterprise_only: true,
        local_only: true,
        ui_only: true
    },
    "roboflow_core/sinks": {
        name: "PLC Ethernet/IP",
        blockPriority: 13,
        icon: "fal fa-microchip",
        section: "industrial",
        short_description: "Communicate with industrial PLCs",
        long_description: "Interface with Allen-Bradley and other Ethernet/IP compatible PLCs.",
        enterprise_only: true,
        local_only: true,
        ui_only: true
    },
    "roboflow_core/modbus_tcp": {
        name: "Modbus TCP",
        blockPriority: 14,
        icon: "fal fa-server",
        section: "industrial",
        short_description: "Write to Modbus TCP devices",
        long_description: "Connect to Modbus TCP devices for industrial automation and monitoring.",
        enterprise_only: true,
        local_only: true,
        ui_only: true
    },

    // Models section
    "roboflow_core/roboflow_object_detection_model": {
        section: "model",
        icon: "far fa-chart-network",
        blockPriority: 0,
        inference: true,
        popular: true
    },
    "roboflow_core/roboflow_instance_segmentation_model": {
        section: "model",
        icon: "far fa-chart-network",
        blockPriority: 1,
        inference: true,
        popular: true
    },
    "roboflow_core/roboflow_classification_model": {
        section: "model",
        icon: "far fa-chart-network",
        blockPriority: 2,
        inference: true,
        popular: true
    },
    "roboflow_core/roboflow_multi_label_classification_model": {
        section: "model",
        icon: "far fa-chart-network",
        blockPriority: 3,
        inference: true
    },
    "roboflow_core/roboflow_keypoint_detection_model": {
        section: "model",
        icon: "far fa-chart-network",
        blockPriority: 4,
        inference: true
    },
    "roboflow_core/open_ai": { section: "model", icon: "fal fa-atom", blockPriority: 5 },
    "roboflow_core/open_ai": {
        section: "model",
        icon: "fal fa-atom",
        blockPriority: 5,
        popular: true
    },
    "roboflow_core/google_gemini": {
        section: "model",
        icon: "fa-brands fa-google",
        blockPriority: 5,
        popular: true
    },
    "roboflow_core/anthropic_claude": {
        section: "model",
        icon: "far fa-a",
        blockPriority: 5,
        popular: true
    },
    "roboflow_core/florence_2": { section: "model", icon: "fal fa-atom", blockPriority: 5.5 },
    "roboflow_core/qwen25vl": {
        section: "model",
        icon: "fal fa-atom",
        blockPriority: 5.5,
        needsGPU: true,
        gpuHeavyDisclaimer:
            "Qwen is the largest model that we have supported in the platform and due to it's large size of 7B parameters our current in-app GPUs might not perform well with it. If you observe the model taking too long/failing, try reducing the length of your input prompts and output texts as well as the image sizes. GPUs like L4/A100 are recommended."
    },
    "roboflow_core/qwen3vl": {
        section: "model",
        icon: "fal fa-atom",
        blockPriority: 5.6,
        needsGPU: true,
        gpuHeavyDisclaimer:
            "Qwen3 VL requires GPU acceleration. If you observe the model taking too long/failing, try reducing the length of your input prompts and output texts as well as the image sizes. GPUs like L4/A100 are recommended."
    },
    "roboflow_core/smolvlm2": {
        section: "model",
        icon: "fal fa-atom",
        blockPriority: 5.5,
        popular: true
    },
    "roboflow_core/lmm": { section: "model", icon: "fal fa-atom", blockPriority: 6 },
    "roboflow_core/lmm_for_classification": {
        section: "model",
        icon: "fal fa-atom",
        blockPriority: 7
    },
    "roboflow_core/google_vision_ocr": {
        section: "model",
        icon: "fa-brands fa-google",
        blockPriority: 7.5
    },
    "roboflow_core/yolo_world_model": {
        section: "model",
        icon: "fal fa-atom",
        blockPriority: 8,
        inference: true
    },
    "roboflow_core/clip_comparison": {
        section: "model",
        icon: "fak fa-message-image",
        blockPriority: 10,
        inference: true
    },
    "roboflow_core/clip_comparison": {
        section: "model",
        icon: "fak fa-message-image",
        blockPriority: 10,
        inference: true
    },
    "roboflow_core/ocr_model": {
        section: "model",
        icon: "far fa-text",
        blockPriority: 11,
        inDevelopment: true,
        inference: true
    },
    "roboflow_core/barcode_detector": {
        section: "model",
        icon: "far fa-barcode",
        blockPriority: 12
    },
    "roboflow_core/qr_code_detector": {
        section: "model",
        icon: "fal fa-qrcode",
        blockPriority: 13,
        opencv: true
    },
    "roboflow_core/gaze": { section: "model", icon: "far fa-eyes", blockPriority: 13.5 },
    "roboflow_core/segment_anything": {
        section: "model",
        icon: "fa-brands fa-meta",
        blockPriority: 9.5,
        needsGPU: true,
        inference: true
    },
    "roboflow_core/stability_ai_inpainting": {
        section: "model",
        icon: "far fa-paintbrush",
        blockPriority: 14
    },

    // Visualizations section
    "roboflow_core/bounding_box_visualization": {
        section: "visualization",
        icon: "far fa-object-group",
        blockPriority: 0,
        warnings: [copyImageWarning],
        supervision: true,
        popular: true
    },
    "roboflow_core/polygon_visualization": {
        section: "visualization",
        icon: "far fa-shapes",
        blockPriority: 1,
        warnings: [copyImageWarning],
        supervision: true,
        popular: true
    },
    "roboflow_core/label_visualization": {
        section: "visualization",
        icon: "far fa-tag",
        blockPriority: 2,
        warnings: [copyImageWarning],
        supervision: true,
        popular: true
    },
    "roboflow_core/classification_label_visualization": {
        section: "visualization",
        icon: "far fa-tags",
        blockPriority: 2.5,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/background_color_visualization": {
        section: "visualization",
        icon: "far fa-fill-drip",
        blockPriority: 3,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/blur_visualization": {
        section: "visualization",
        icon: "fad fa-glasses",
        blockPriority: 4,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/circle_visualization": {
        section: "visualization",
        icon: "far fa-circle",
        blockPriority: 5,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/color_visualization": {
        section: "visualization",
        icon: "far fa-palette",
        blockPriority: 6,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/corner_visualization": {
        section: "visualization",
        icon: "far fa-expand",
        blockPriority: 7,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/crop_visualization": {
        section: "visualization",
        icon: "far fa-crop-alt",
        blockPriority: 8,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/dot_visualization": {
        section: "visualization",
        icon: "fad fa-dot-circle",
        blockPriority: 9,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/ellipse_visualization": {
        section: "visualization",
        icon: "fad fa-ring",
        blockPriority: 10,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/halo_visualization": {
        section: "visualization",
        icon: "far fa-lightbulb-on",
        blockPriority: 11,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/mask_visualization": {
        section: "visualization",
        icon: "far fa-mask",
        blockPriority: 12,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/pixelate_visualization": {
        section: "visualization",
        icon: "fad fa-grid",
        blockPriority: 13,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/triangle_visualization": {
        section: "visualization",
        icon: "far fa-triangle",
        blockPriority: 14,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/polygon_zone_visualization": {
        section: "visualization",
        icon: "far fa-hexagon",
        blockPriority: 15,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/line_counter_visualization": {
        section: "visualization",
        icon: "far fa-arrow-down-up-across-line",
        blockPriority: 15,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/model_comparison_visualization": {
        section: "visualization",
        icon: "far fa-not-equal",
        blockPriority: 16,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/trace_visualization": {
        section: "visualization",
        icon: "far fa-scribble",
        blockPriority: 17,
        warnings: [copyImageWarning],
        supervision: true
    },
    "roboflow_core/reference_path_visualization": {
        section: "visualization",
        icon: "fas fa-road",
        blockPriority: 18,
        warnings: [copyImageWarning],
        supervision: true
    },

    // Flow Control section
    "roboflow_core/continue_if": {
        section: "flow_control",
        icon: "fak fa-branching",
        blockPriority: 0,
        popular: true
    },
    "roboflow_core/detections_filter": {
        section: "flow_control",
        icon: "far fa-filter",
        blockPriority: 1,
        popular: true
    },
    "roboflow_core/rate_limiter": {
        section: "flow_control",
        icon: "far fa-clock",
        blockPriority: 2,
        popular: true
    },
    "roboflow_core/delta_filter": {
        section: "flow_control",
        icon: "far fa-arrow-progress",
        blockPriority: 3
    },
    "roboflow_core/detections_consensus": {
        section: "flow_control",
        icon: "fak fa-circles-overlap",
        blockPriority: 4
    },

    // Data Sink section
    "roboflow_core/roboflow_dataset_upload": {
        section: "data_storage",
        icon: "fal fa-upload",
        blockPriority: 0,
        popular: true
    },
    "roboflow_core/webhook_sink": {
        section: "data_storage",
        icon: "fal fa-webhook",
        blockPriority: 1,
        popular: true
    },
    "roboflow_core/local_file_sink": {
        section: "data_storage",
        icon: "fal fa-file",
        blockPriority: 3,
        popular: true
    },
    "roboflow_core/csv_formatter": {
        section: "data_storage",
        icon: "fal fa-file-csv",
        blockPriority: 2,
        popular: true
    },
    "roboflow_core/data_aggregator": {
        section: "data_storage",
        icon: "fal fa-database",
        blockPriority: 4,
        popular: true
    },

    // Notifications section
    "roboflow_core/email_notification": {
        section: "notifications",
        icon: "far fa-envelope",
        blockPriority: 0,
        popular: true
    },

    // Transformations section
    "roboflow_core/dynamic_crop": {
        section: "transformation",
        icon: "far fa-crop-alt",
        blockPriority: 0,
        popular: true
    },
    "roboflow_core/absolute_static_crop": {
        section: "transformation",
        icon: "far fa-crop-alt",
        blockPriority: 1
    },
    "roboflow_core/relative_statoic_crop": {
        section: "transformation",
        icon: "far fa-crop-alt",
        blockPriority: 2
    },
    "roboflow_core/detection_offset": {
        section: "transformation",
        icon: "fal fa-distribute-spacing-horizontal",
        blockPriority: 3
    },
    "roboflow_core/detections_transformation": {
        section: "transformation",
        icon: "fal fa-expand",
        blockPriority: 4
    },
    "roboflow_core/bounding_rect": {
        section: "transformation",
        icon: "fal fa-rectangles-mixed",
        blockPriority: 5
    },
    "roboflow_core/stitch_images": {
        section: "transformation",
        icon: "far fa-object-union",
        blockPriority: 6,
        opencv: true
    },

    // Classical Computer Vision section
    "roboflow_core/image_preprocessing": {
        section: "classical_cv",
        icon: "far fa-image",
        blockPriority: 0,
        opencv: true
    },
    "roboflow_core/template_matching": {
        section: "classical_cv",
        icon: "far fa-crosshairs",
        blockPriority: 0.5,
        opencv: true
    },
    "roboflow_core/dominant_color": {
        section: "classical_cv",
        icon: "far fa-palette",
        blockPriority: 1,
        opencv: true
    },
    "roboflow_core/pixel_color_count": {
        section: "classical_cv",
        icon: "far fa-tally",
        blockPriority: 2,
        opencv: true
    },
    "roboflow_core/sift_comparison": {
        section: "classical_cv",
        icon: "far fa-magnifying-glass-arrows-rotate",
        blockPriority: 3,
        opencv: true
    },
    "roboflow_core/sift_comparison": {
        section: "classical_cv",
        icon: "far fa-magnifying-glass-arrows-rotate",
        blockPriority: 3,
        opencv: true
    },
    "roboflow_core/sift": {
        section: "classical_cv",
        icon: "far fa-grid-2-plus",
        blockPriority: 4,
        opencv: true
    },
    "roboflow_core/image_blur": {
        section: "classical_cv",
        icon: "far fa-droplet",
        blockPriority: 5,
        opencv: true
    },
    "roboflow_core/threshold": {
        section: "classical_cv",
        icon: "far fa-circle-half-stroke",
        blockPriority: 6,
        opencv: true
    },
    "roboflow_core/convert_grayscale": {
        section: "classical_cv",
        icon: "far fa-palette",
        blockPriority: 7,
        opencv: true
    },
    "roboflow_core/contours_detection": {
        section: "classical_cv",
        icon: "far fa-border-all",
        blockPriority: 4,
        opencv: true
    },
    "roboflow_core/camera_focus": {
        section: "classical_cv",
        icon: "far fa-aperture",
        blockPriority: 8,
        opencv: true
    },
    "roboflow_core/camera_focus": {
        section: "classical_cv",
        icon: "far fa-aperture",
        blockPriority: 9,
        opencv: true
    },
    "roboflow_core/size_measurement": {
        section: "classical_cv",
        icon: "far fa-ruler",
        blockPriority: 10,
        opencv: true
    },
    "roboflow_core/distance_measurement": {
        section: "classical_cv",
        icon: "far fa-ruler-triangle",
        blockPriority: 11,
        opencv: true
    },

    // Advanced section
    "roboflow_core/property_definition": {
        section: "advanced",
        icon: "fal fa-gear-code",
        blockPriority: 0,
        popular: true
    },
    "roboflow_core/expression": {
        section: "advanced",
        icon: "fal fa-code",
        blockPriority: 1,
        inDevelopment: true
    },
    "roboflow_core/perspective_correction": {
        section: "advanced",
        icon: "fal fa-toolbox",
        blockPriority: 2,
        opencv: true
    },
    "roboflow_core/dynamic_zone": {
        section: "advanced",
        icon: "fal fa-square-dashed",
        blockPriority: 3,
        opencv: true
    },
    "DetectionsRateLimiter": { section: "advanced", icon: "fal fa-turtle", blockPriority: 4 },
    "roboflow_core/detections_classes_replacement": {
        section: "advanced",
        icon: "fal fa-arrow-right-arrow-left",
        blockPriority: 5
    },
    "roboflow_core/json_parser": {
        section: "advanced",
        icon: "fal fa-table-tree",
        blockPriority: 5
    },
    "roboflow_core/vlm_as_classifier": {
        section: "advanced",
        icon: "fal fa-tags",
        blockPriority: 5
    },
    "roboflow_core/vlm_as_detector": {
        section: "advanced",
        icon: "fal fa-object-ungroup",
        blockPriority: 5
    },
    "roboflow_core/dimension_collapse": {
        section: "advanced",
        icon: "fal fa-layer-minus",
        blockPriority: 6,
        inDevelopment: true
    },
    "roboflow_core/first_non_empty_or_default": {
        section: "advanced",
        icon: "fal fa-empty-set",
        blockPriority: 7,
        inDevelopment: true
    },
    "roboflow_core/roboflow_custom_metadata": {
        section: "advanced",
        icon: "fal fa-analytics",
        blockPriority: 8
    },
    "roboflow_core/model_monitoring_inference_aggregator": {
        section: "advanced",
        icon: "fal fa-chart-line",
        blockPriority: 8.5
    },
    "roboflow_core/image_slicer": {
        section: "advanced",
        icon: "fal fa-scissors",
        blockPriority: 9,
        supervision: true
    },
    "roboflow_core/detections_stitch": {
        section: "advanced",
        icon: "fal fa-reel",
        blockPriority: 10,
        supervision: true
    },

    // Video section
    "roboflow_core/byte_tracker": {
        section: "video",
        icon: "far fa-location-crosshairs",
        blockPriority: 0
    },
    "roboflow_core/time_in_zone": { section: "video", icon: "far fa-timer", blockPriority: 1 },
    "roboflow_core/line_counter": {
        section: "video",
        icon: "far fa-arrow-down-up-across-line",
        blockPriority: 2
    },
    "roboflow_core/path_deviation_analytics": {
        section: "video",
        icon: "far fa-road",
        blockPriority: 3
    },
    "roboflow_core/stabilize_detections": {
        section: "video",
        icon: "fas fa-waveform-lines",
        blockPriority: 4
    },

    // alternate types needed to correctly render blocks while the manifest is fetched
    "ObjectDetectionModel": { section: "model", icon: "far fa-chart-network", blockPriority: 0 },
    "InstanceSegmentationModel": {
        section: "model",
        icon: "far fa-chart-network",
        blockPriority: 1
    },
    "ClassificationModel": { section: "model", icon: "far fa-chart-network", blockPriority: 2 },
    "MultiLabelClassificationModel": {
        section: "model",
        icon: "far fa-chart-network",
        blockPriority: 3
    },
    "KeypointDetectionModel": { section: "model", icon: "far fa-chart-network", blockPriority: 4 },
    "Crop": { section: "transformation", icon: "far fa-crop-alt", blockPriority: 0 },

    "custom_python_block": { section: "custom", icon: "far fa-code", blockPriority: 0 }
};

const CUSTOM_PYTHON_BLOCK = {
    block_schema: {
        additionalProperties: true,
        block_type: "sink",
        license: "Apache-2.0",
        properties: {
            type: {
                const: "roboflow_core/roboflow_dataset_upload",
                enum: ["roboflow_core/roboflow_dataset_upload"],
                title: "Type",
                type: "string"
            }
        },
        required: ["type"],
        short_description: "Create a block to execute custom Python code.",
        title: "BlockManifest"
    },
    block_source: "custom_block",
    fully_qualified_block_class_name: "",
    human_friendly_block_name: "Custom Python Block",
    manifest_type_identifier: "custom_python_block",
    manifest_type_identifier_aliases: [],
    execution_engine_compatibility: ">=1.0.0,<2.0.0",
    section: "custom",
    icon: "far fa-code"
};

module.exports = {
    BLOCK_SECTIONS,
    BLOCK_UI_SPECIFICATION,
    CUSTOM_PYTHON_BLOCK,
    detection_types,
    detection_types_array,
    classification_types
};
